import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Center, Spinner, Text, VStack } from "@chakra-ui/react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { clearStoredAuthSession } from "../lib/authSession";
import { UserProfileProvider } from "./UserProfileContext";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState({
    user: null,
    ready: false,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setSession({ user: null, ready: false, error: null });
    setAttempt((previous) => previous + 1);
  }, []);

  useEffect(() => {
    const auth = getAuth();
    let active = true;
    // Remove obsolete tab mirrors; Firebase remains usable when browser
    // storage is unavailable because identity no longer depends on these keys.
    try { clearStoredAuthSession(); } catch (_) { /* Legacy cleanup is best effort. */ }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!active || auth.currentUser?.uid !== user?.uid) return;
      setSession({ user, ready: true, error: null });
    }, (error) => {
      if (active) setSession({ user: null, ready: true, error });
    });
    return () => { active = false; unsubscribe(); };
  }, [attempt]);

  const value = useMemo(() => ({ ...session, retry }), [retry, session]);

  const uid = session.user?.uid || null;
  let content = <UserProfileProvider uid={uid}>{children}</UserProfileProvider>;
  if (!session.ready) {
    content = (
      <Center minH="100vh">
        <Spinner size="xl" color="pt-blue" label="Restoring your session" />
      </Center>
    );
  } else if (session.error) {
    content = (
      <Center minH="100vh">
        <VStack spacing={4} role="alert">
          <Text>We couldn't restore your session. Please try again.</Text>
          <Button onClick={retry} colorScheme="blue">Try again</Button>
        </VStack>
      </Center>
    );
  }

  return <AuthContext.Provider value={value}>{content}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};

// Protected routes discard account-owned drafts and data on an account change.
// Public login/registration forms must survive their own sign-in notification
// so profile-setup failures remain visible in the form that submitted them.
export const AccountSession = ({ children }) => {
  const { user } = useAuth();
  return <React.Fragment key={user?.uid || "signed-out"}>{children}</React.Fragment>;
};
